/**
 * Fallback values for the pricing fields of AppSettings (`key: 'default'`), in dollars unless named otherwise.
 * The stored settings document always wins; these apply only to fields it does not contain.
 * Customer fee values follow docs/business/legal-compliance-and-fee-regulations.md §1.3 and DEFAULT_DYNAMIC_POLICY.
 * Driver payout itself is computed from `DRIVER_PAYOUT_*` (driverPayout.math.ts); `driverMileageRate`
 * here only feeds the checkout driver-cost estimate and must match that payout default.
 */
export const PRICING_SETTINGS_DEFAULTS = Object.freeze({
  routeFee: 2,
  /** Customer miles covered by the base delivery fee; per-mile charges start after these. Driver mileage pay still starts at mile 0. */
  distanceIncludedMiles: 2,
  maxDeliveryRadiusMiles: 25,
  customerPerMileRate: 0.7,
  customerPerItemFee: 0.25,
  /** Items at or below this count never pay the large-order fee. */
  largeOrderIncludedItems: 19,
  /** Rate step per bracket: items in the first bracket pay this each, the second bracket pays twice this, and so on. */
  largeOrderPerItemFee: 0.1,
  largeOrderBracketItems: 10,
  heavyItemFeePerUnit: 1.5,
  heavyItemThresholdPoints: 10,
  extraStoreBaseFee: 2.99,
  smallOrderThreshold: 15,
  /** Carts with fewer items than this pay the small-order fee even above the dollar threshold. */
  smallOrderItemThreshold: 5,
  smallOrderFee: 1.99,
  /** Hold per custom request when no reliable retail price is found. */
  customRequestDeposit: 30,
  /** Added on top of a found retail price for the custom request hold. */
  customRequestDepositBufferPercent: 15,
  driverBaseMinutes: 4,
  driverMinutesPerItem: 0.75,
  driverHourlyFloorRate: 20,
  driverMileageRate: 0.7,
  driverMinDropoffFloor: 5,
  minPlatformFee: 1.5,
  platformFeePercent: 5,
  lateNightConvenienceFee: 1.99,
  standaloneReturnFee: 1.99
} as const);

export type PricingSettingsDefaults = typeof PRICING_SETTINGS_DEFAULTS;

/**
 * Escalating large-order fee: items past `includedItems` pay `stepCents` each in the first bracket of
 * `bracketItems`, `2 × stepCents` in the next bracket, and so on. Only the items inside a bracket pay its rate.
 */
export const computeLargeOrderFeeCents = (
  itemCount: number,
  includedItems: number,
  stepCents: number,
  bracketItems: number
): number => {
  const count = Math.max(0, Math.floor(itemCount));
  const included = Math.max(0, Math.floor(includedItems));
  const step = Math.max(0, Math.round(stepCents));
  const bracket = Math.max(1, Math.floor(bracketItems));
  const extra = count - included;
  if (extra <= 0 || step === 0) return 0;
  let total = 0;
  for (let position = 0; position < extra; position += 1) {
    total += step * (Math.floor(position / bracket) + 1);
  }
  return total;
};
